import { http, getError, catchError } from "services/http"
import i18next from "i18next"
import { Entity, User, ErrorFn, ErrorResponse } from "models"

/**********************
 **** SIGN IN ****
 **********************/

export type SignInAnswer = Entity

export const signIn = async (
	email: string,
	password: string,
	operator: string
): Promise<{ res: SignInAnswer } & ErrorFn> => {
	const userErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
			case "MailNotAvailableException": // Mail is not available.
				return {
					code: error?.code,
					message: i18next.t("error_not_authorized", { ns: "form" }),
				}
			default:
				// La connexion via VPN est refusée en prod (pas en preprod).
				return {
					code: error?.code,
					message: i18next.t("connexion_refused", { ns: "form" }),
				}
		}
	}
	const res = await http
		.post<SignInAnswer>(
			"obikeapi/account/signin",
			{
				operator: operator,
				mail: email,
				password: password,
			},
			{}
		)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as SignInAnswer,
		getError: getError(res as unknown as ErrorResponse, userErrors),
	}
}

/**********************
 **** CHECK MAIL ****
 **********************/

export type SignUpCheckMailAnswer = Record<string, never>

export const signUpCheckMail = async (mail: string, operator: string): Promise<ErrorFn> => {
	const signUpCheckMailErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "MailNotAvailableException": // Mail is not available.
				return {
					code: error?.code,
					message: i18next.t("mail_error_MailNotAvailableException", { ns: "form" }),
				}
			case "TooManyRequestsException": // This entity is already registered.
				return {
					code: error?.code,
					message: i18next.t("account_already_registered", {
						ns: "form",
					}),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.post<SignUpCheckMailAnswer>(
			"obikeapi/account/check_mail",
			{
				mail: mail,
				operator: operator,
			},
			{}
		)
		.catch((e) => catchError(e))

	return {
		getError: getError(res as unknown as ErrorResponse, signUpCheckMailErrors),
	}
}

/**********************
 **** SIGN UP ****
 **********************/

type SignUpProps = User & {
	cgu_version: string
	operator_notifications_accepted?: string
	ocode_notifications_accepted?: string
}

export type SignUpAnswer = Record<string, never>

export const signUp = async (newUser: SignUpProps): Promise<ErrorFn> => {
	const createAccountErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "MailNotAvailableException": // Mail is not available.
				return {
					code: error?.code,
					message: i18next.t("mail_error_MailNotAvailableException", {
						ns: "form",
					}),
				}
			case "TooManyRequestsException": // This entity is already registered.
				return {
					code: error?.code,
					message: i18next.t("account_already_registered", {
						ns: "form",
					}),
				}
			case "NotAuthorizedException": // Code expired or invalid.
				return {
					code: error?.code,
					message: i18next.t("expired_code", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", {
						ns: "form",
					}),
				}
		}
	}

	const res = await http
		.post<SignUpAnswer>("obikeapi/account/create_account", newUser, {})
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, createAccountErrors),
	}
}

/**********************
 **** INIT PASSWORD ***
 **********************/

type SignInInitPasswordAnswer = Record<string, never>

export const signInInitPassword = async (mail: string, operator: string): Promise<ErrorFn> => {
	const signInInitPasswordErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "TooManyRequestsException":
				return {
					code: error?.code,
					message: i18next.t("code_already_received", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", {
						ns: "form",
					}),
				}
		}
	}

	const res = await http
		.get<SignInInitPasswordAnswer>(
			`obikeapi/account/initpassword?mail=${mail}&operator=${operator}`,
			{}
		)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, signInInitPasswordErrors),
	}
}

/**********************
 **** RESET PASSWORD ***
 **********************/

export type SignInResetPassword = {
	operator: string
	recovery_password: string
	password: string
	mail: string
}

export const signInResetPassword = async (
	mail: string,
	operator: string,
	code: string,
	newpassword: string
): Promise<ErrorFn> => {
	const signInResetPasswordErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException": // Code expired or invalid.
				return {
					code: error?.code,
					message: i18next.t("expired_code", { ns: "form" }),
				}
			case "MailNotAvailableException": // Mail is not available.
				return {
					code: error?.code,
					message: i18next.t("mail_error_invalid", { ns: "form" }),
				}
			case "EmailUnknownException": // Unknown email or Account disabled.
				return {
					code: error?.code,
					message: i18next.t("password_update_error_EmailUnknownException", {
						ns: "form",
					}),
				}
			case "TooManyRequestsException": // This entity is already registered.
				return {
					code: error?.code,
					message: i18next.t("account_already_registered", {
						ns: "form",
					}),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", {
						ns: "form",
					}),
				}
		}
	}

	const res = await http
		.post<SignInResetPassword>(
			"obikeapi/account/reset_password",
			{
				recovery_password: code,
				operator: operator,
				newpassword: newpassword,
				mail: mail,
			},
			{}
		)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, signInResetPasswordErrors),
	}
}
